/**
 * Gmail Folders Cache Service
 * Manages offline caching and real-time sync of Gmail folders/labels
 * Mirrors Gmail folder structure synced by backend Cloud Function
 */

import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase.config';
import { useGmailFolders } from '../stores';
import { getTenantId, getStoreId } from './userStoreCache';

// localStorage caching removed - using Firestore persistent cache only

/**
 * Initialize Gmail folders cache system
 * Sets up real-time listener to watch tenant-specific gmailFolders document
 * @returns {Function} Unsubscribe function
 */
export const initializeGmailFoldersCache = () => {
  // Get tenant/store context
  const tenantId = getTenantId();
  const storeId = getStoreId();

  // Firestore persistent cache handles offline data automatically

  // Set up real-time listener on the gmailFolders document
  let gmailFoldersDocRef;
  let foldersPath;
  
  if (tenantId && storeId) {
    // Multi-tenant path - add 'gmail' collection to make 6 segments (even number)
    // Path: tenants/{id}/stores/{id}/gmail/folders
    gmailFoldersDocRef = doc(db, 'tenants', tenantId, 'stores', storeId, 'gmail', 'folders');
    foldersPath = `tenants/${tenantId}/stores/${storeId}/gmail/folders`;
  } else {
    // Legacy global path (fallback for single-tenant or migration period)
    gmailFoldersDocRef = doc(db, 'system', 'gmailFolders');
    foldersPath = 'system/gmailFolders';
    console.warn(`⚠️ No tenant/store context - using legacy path: ${foldersPath}`);
  }
  
  const unsubscribe = onSnapshot(
    gmailFoldersDocRef,
    (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        const folders = data.folders || [];
        
        console.log(`📁 Received ${folders.length} Gmail folder(s) from Firestore`);
        console.log(`   Path: ${foldersPath}`);
        console.log(`   Last sync: ${data.lastSync}`);
        if (tenantId && storeId) {
          console.log(`   Tenant: ${tenantId}, Store: ${storeId}`);
        }
        
        // Update Zustand store with the latest folders
        const setFolders = useGmailFolders.getState().setFolders;
        setFolders(folders);
        
        console.log('✅ Gmail folders cache updated successfully');
      } else {
        console.warn(`⚠️ Gmail folders document does not exist at ${foldersPath}`);
        // Clear Zustand store if document doesn't exist
        useGmailFolders.getState().clearFolders();
      }
    },
    (error) => {
      console.error('❌ Error in Gmail folders subscription:', error);
    }
  );

  return unsubscribe;
};

/**
 * Get Gmail folders from cache (synchronous)
 * @returns {Array} Array of folder objects
 */
export const getGmailFoldersFromCache = () => {
  try {
    return useGmailFolders.getState().folders;
  } catch (error) {
    console.error('Error reading Gmail folders from store:', error);
    return [];
  }
};

// localStorage caching functions removed - using Firestore persistent cache only

/**
 * Clear Gmail folders cache
 */
export const clearGmailFoldersCache = () => {
  useGmailFolders.getState().clearFolders();
  console.log('🗑️ Gmail folders cache cleared');
};

/**
 * Get last cache update timestamp
 * @returns {Date|null} Last update date or null
 */
export const getGmailFoldersCacheTimestamp = () => {
  // No longer tracking timestamp - using Firestore cache
  return null;
};

